<template>
    <app-layout title="Pricing">
        <form @submit.prevent="handleSubmit">
            <page-header>
                <template #title>Pricing</template>

                <template #actions>
                    <!-- Export button / dropdown -->
                    <jet-dropdown align="right" width="48">
                        <template #trigger>
                            <jet-tertiary-button size="small">
                                <img src="/images/icons/excel.svg" width="16" height="16" alt="Export">
                                <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Export">
                            </jet-tertiary-button>
                        </template>
                        <template #content>
                            <div v-for="level in pricingLevels" :key="level.id"
                                class="px-4 py-2 hover:bg-gray cursor-pointer text-sm"
                                @click="handleExport(level)"
                            >
                                {{ level.title }}
                            </div>
                        </template>
                    </jet-dropdown>

                    <jet-button v-show="updatedProducts.length > 0" type="submit" class="leading-none" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button v-show="updatedProducts.length > 0" class="leading-none" :disabled="form.processing" :href="route('orders.index')">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </page-header>

            <div class="px-8">
                <div class="flex gap-x-2 items-center mb-2">
                    <!-- Search / Filters -->
                    <jet-input type="text" class="w-4/12 h-7 text-base" v-model="mainForm.search" @input="handleFilter" placeholder="Search..."  />

                    <div class="w-48">
                        <multiselect  class="multi-custom h-7 text-base"
                            v-model="mainForm.category_id"
                            :options="categories"
                            valueProp="id"
                            trackBy="slug"
                            label="title"
                            :searchable="true"
                            placeholder="Category"
                            @change="handleFilter"
                        >
                            <template v-slot:option="{ option }">
                                <span :class="{ 'ml-5' : option.parent_id != null }">{{ option.title }} </span>
                            </template>
                        </multiselect>
                    </div>

                    <div class="w-48">
                        <multiselect class="multi-custom h-7 text-base"
                            v-model="mainForm.enabled"
                            :options="enabledOptions"
                            placeholder="All Items"
                            @change="handleFilter"
                            :classes="{singleLabel: 'w-full px-2'}"
                        />
                    </div>

                    <div class="w-48">
                        <multiselect class="multi-custom h-7 text-base"
                            v-model="mainForm.purchaser_id"
                            :options="purchasers"
                            label="name"
                            valueProp="id"
                            trackBy ="id"
                            placeholder="All Purchasers"
                            @change="handleFilter"
                            :classes="{singleLabel: 'w-full px-2'}"
                        />
                    </div>

                    <div class="flex items-center gap-x-2">
                        <toggle v-model="mainForm.unsaved_only" />
                        <jet-label for="unsaved" value="Unsaved Changes Only" class="mb-0" />
                    </div>

                    <div>
                        <toggle v-model="mainForm.is_wholesale"   @change="handleFilter" />
                        <jet-label for="is_wholesale" value="Wholesale only" class="mb-0 ml-2 inline-block" />
                    </div>

                    <div>
                        <toggle v-model="mainForm.has_market_cost"   @change="handleFilter" />
                        <jet-label for="has_market_cost" value="Market cost only" class="mb-0 ml-2 inline-block" />
                    </div>

                    <jet-tertiary-button type="button" class="ml-4 py-1 h-7 self-start" @click="handleReset" >
                        Reset
                    </jet-tertiary-button>
                </div>

                <div class="flex space-x-4 my-1">
                    <div><span class="font-bold">$: </span> Set dollar amount</div>
                    <div><span class="font-bold">$+: </span>Dollars above cost</div>
                    <div><span class="font-bold">%: </span> Percentage below MSRP</div>
                    <div><span class="font-bold">M%: </span> Margin percentage</div>
                </div>

                <div class="rounded-t overflow-x-hidden overflow-y-auto border border-b-0" ref="scrollDiv" scroll-region>
                    <table class="table w-full">
                        <thead class="sticky top-0 z-10">
                            <tr>
                                <th class="text-left">
                                    <input v-model="selectedAll" type="checkbox" @change="selectAll" />
                                </th>
                                <sortable-header class="text-left whitespace-nowrap" @sort="handleSort" sort-by="id" :direction="sort_direction" :column="sort_column">Item #</sortable-header>
                                <sortable-header class="text-left whitespace-nowrap" @sort="handleSort" sort-by="related_master_product_id" :direction="sort_direction" :column="sort_column">Master #</sortable-header>
                                <sortable-header class="text-left whitespace-nowrap" style="min-width: 300px;" @sort="handleSort" sort-by="title" :direction="sort_direction" :column="sort_column">Product</sortable-header>
                                <th class="text-center">Active</th>
                                <th class="text-center">Local</th>
                                <th class="text-center whitespace-nowrap">Featured Product</th>
                                <th class="text-center whitespace-nowrap">Wholesale Special (Lead Time)</th>
                                <th class="text-center">Oversell</th>
                                <th class="text-center whitespace-nowrap">Qty on Hand</th>
                                <th v-if="hasMarketCostProducts" class="text-center">Market Cost</th>
                                <th class="text-center">Cost</th>
                                <th class="text-center whitespace-nowrap">Price (MSRP)</th>
                                <th v-for="level in pricingLevels" :key="level.id" class="text-center whitespace-nowrap">
                                    <div class="flex gap-x-2 items-center justify-center">
                                        <span>{{ level.title }}</span>

                                        <a href="#" v-if="isEditingPricingLevel(level.id)" @click.prevent="disablePricingLevelEdit(level.id)" title="Disable">
                                            <img src="/images/icons/x-square.svg" width="18" height="18" style="max-width: none; max-height: none;" />
                                        </a>

                                        <a href="#" v-else @click.prevent="enablePricingLevelEdit(level.id)" title="Enable">
                                            <img src="/images/icons/edit-square.svg" width="18" height="18" style="max-width: none; max-height: none;" class="mb-1" />
                                        </a>
                                    </div>
                                </th>
                                <th class="text-left "></th>
                            </tr>
                        </thead>
                        <tbody>
                            <product-pricing
                                v-for="(productForm, productIndex) in form.products"
                                :key="`products.${productForm.product.id}`"
                                :product="productForm.product"
                                :form="productForm"
                                :filters="mainForm"
                                :categories="categories"
                                :selected="selected.findIndex(id => id === productForm.id) !== -1"
                                :editing="editing.findIndex(id => id === productForm.id) !== -1"
                                :editing-price-levels="editingPriceLevels"
                                :errors="$page.props.errors"
                                :product-index="productIndex"
                                :has-market-cost-products="hasMarketCostProducts"
                                :updated-products="updatedProducts"
                                @select-toggle="handleSelectToggle"
                                @edit-toggle="handleEditToggle"
                                @product-update="handleProductUpdate"
                                @price-level-update="handlePriceLevelUpdate"
                            />
                        </tbody>
                    </table>
                </div>

                <pagination class="inline-flex" :links="products.links" />

                <div class="my-2 sticky bottom-4">
                    <jet-secondary-button v-show="selected.length" @click="showBulkEditModal = true;">
                        Edit ({{ selected.length }} Selected)
                    </jet-secondary-button>
                </div>
            </div>

        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="updatedProducts.length > 0"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(route('products.edit', {
                    product: this.product.id,
                    return: route('pricing.index')
                })
            )"
        />

        <bulk-edit-modal
            :show="showBulkEditModal"
            :pricing-levels="pricingLevels"
            @close="showBulkEditModal = false"
            @updated="handleBulkUpdate"
        />
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import ProductPricing from '@/Pages/Pricing/Partials/ProductPricing';
import BulkEditModal from '@/Pages/Pricing/Partials/BulkEditModal';
import PageHeader from '@/Layouts/partials/PageHeader';
import JetButton from '@/Jetstream/Button';
import JetDropdown from '@/Jetstream/Dropdown';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import { calculatePricingLevel } from '@/helper';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';
import Pagination from '@/Jetstream/Pagination';
import SortableHeader from '@/Shared/SortableHeader';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            search: props.filters.search,
            category_id: props.filters.category_id,
            purchaser_id: props.filters.purchaser_id,
            is_wholesale: props.filters.is_wholesale === 'true' ? true : false,
            has_market_cost: props.filters.has_market_cost === 'true' ? true : false,
            enabled: props.filters.enabled,
            unsaved_only: false,
            sort_column: props.sort_column,
            sort_direction: props.sort_direction,
        });

        return { mainForm }
    },

    components: {
        AppLayout,
        ProductPricing,
        PageHeader,
        JetButton,
        JetDropdown,
        JetSecondaryButton,
        JetTertiaryButton,
        JetInput,
        JetLabel,
        Multiselect,
        Toggle,
        BulkEditModal,
        UnsavedChangesModal2,
        Pagination,
        SortableHeader,
    },

    props: ['products', 'pricingLevels', 'categories', 'purchasers' , 'fetchedAt', 'filters', 'sort_column', 'sort_direction'],

    data() {
        const formData = {
            fetchedAt: this.fetchedAt,
            products: this.resetFormData(),
        };

        return {
            editingPriceLevels: [],
            editing: [],
            selected: [],
            selectedAll: false,
            showBulkEditModal: false,
            showUnsavedChangesModal: false,

            form: this.$inertia.form(formData),
            formRoute: this.route('pricing.update'),

            enabledOptions: [
                { value: 1, label: 'Active Items' },
                { value: 0, label: 'Inactive Items' },
            ],
        }
    },

    computed: {
        updatedProducts () {
            return this.form.products.filter(p => p.hasUpdated);
        },

        hasMarketCostProducts() {
            // Check if any product in the current page is in a market cost category
            return this.products.data.some(product => {
                const cat = this.categories.find(c => c.id === product.category_id);
                if (!cat) return false;
                const marketCostCategories = [99];
                return marketCostCategories.includes(cat.id) || marketCostCategories.includes(cat.parent_id);
            });
        }
    },

    methods: {
        handleSort (sort) {
            this.mainForm.sort_column = sort.column;
            this.mainForm.sort_direction = sort.direction;
            this.handleMainForm();
        },

        handleFilter () {
            this.handleMainForm();
        },

        handleMainForm: _.debounce(function () {
            this.$inertia.get(this.route('pricing.index'), this.mainForm, {
                preserveState: true,
                onSuccess: () => { this.onSuccess(); }
            });
        }, 250),

        // Build payload with only product id and modified fields
        handleSubmit () {
            if (this.form.processing) {
                return;
            }

            this.form.processing = true;

            const products = this.updatedProducts.map(p => {
                const payload = { id: p.id };
                p.dirtyFields.forEach(field => {
                    payload[field] = p[field];
                });
                if (p.dirtyFields.includes('price')) {
                    payload.pricing_levels = p.pricing_levels;
                } else if (p.dirtyPricingLevelIds.length) {
                    payload.pricing_levels = p.pricing_levels.filter(pl =>
                        p.dirtyPricingLevelIds.includes(pl.pricing_level_id)
                    );
                }
                return payload;
            });

            const data = {
                fetchedAt: this.form.fetchedAt,
                products,
            };

            this.$inertia.post(this.route('pricing.update'), data, {
                onSuccess: () => {
                    this.onSuccess();
                },

                onFinish: () => {
                    this.form.processing = false;
                },
            });
        },

        onSuccess() {
            this.editingPriceLevels = [];
            this.form.products = this.resetFormData();
            this.form.fetchedAt = this.fetchedAt;

            // nextTick needed otherwise hasUpdated is still set to true by the watcher
            this.$nextTick(() => {
                this.form.products.forEach(p => {
                    // Reset the hasUpdated to override the watcher on initial reset
                    p.hasUpdated = false
                });
            });

            this.selected.length = 0;
            this.editing.length = 0;
        },

        handleReset() {
            this.mainForm.search = '';
            this.mainForm.category_id = null;
            this.mainForm.is_wholesale = false;
            this.mainForm.has_market_cost = false;
            this.mainForm.enabled = null;
            this.mainForm.unsaved_only = false;
            this.mainForm.search = '';
            this.mainForm.sort_column = 'related_master_product_id';
            this.mainForm.sort_direction = 'desc';
            this.mainForm.purchaser_id = null;
            this.handleFilter();
        },

        handleExport(level) {
            let levelNum = level.title.includes('Retail')
                ? level.id
                : level.title.toLowerCase().replace('level', '').replace(' ','');
            window.open('/pricing/' + levelNum + '/export');
        },

        handleSelectToggle (payload) {
            if (payload.selected) {
                this.selected.push(payload.id);
            } else {
                const index = this.selected.findIndex(s => s === payload.id);

                if (index !== -1) {
                    this.selected.splice(index, 1);
                }
            }
        },

        handleEditToggle (payload) {
            if (payload.editing) {
                this.editing.push(payload.id)
            } else {
                const index = this.editing.findIndex(s => s === payload.id);

                if (index !== -1) {
                    this.editing.splice(index, 1);
                }
            }
        },

        handleBulkUpdate (payload) {
            this.selected.forEach(productId => {
                let productForm = this.form.products.find(p => p.id === productId);

                if (payload.priceEnabled) {
                    productForm.price = payload.price;
                    if (!productForm.dirtyFields.includes('price')) productForm.dirtyFields.push('price');
                }
                if (payload.priceTwoPlusEnabled) {
                    productForm.price_2plus = payload.price_2plus;
                    if (!productForm.dirtyFields.includes('price_2plus')) productForm.dirtyFields.push('price_2plus');
                }
                if (payload.priceEnabled || payload.priceTwoPlusEnabled) {
                    this.handleProductUpdate(productForm);
                }

                payload.pricing_levels.forEach(pl => {
                    if (pl.enabled) {
                        const index = productForm.pricing_levels.findIndex(fl => fl.pricing_level_id === pl.pricing_level_id);

                        if (index !== -1) {
                            const effectiveCost = productForm.market_cost ?? productForm.cost;
                            pl.price = this.calculatePricingLevel(pl, effectiveCost, productForm.price);
                            productForm.hasUpdated = true;
                            if (!productForm.dirtyPricingLevelIds.includes(pl.pricing_level_id)) {
                                productForm.dirtyPricingLevelIds.push(pl.pricing_level_id);
                            }

                            productForm.pricing_levels[index] = {
                                ...pl,
                                product_id: productForm.product.id,
                            };
                        }
                    }
                });
            });
        },

        handleProductUpdate ({ market_cost, cost, price, price_2plus, product }) {
            let productForm = this.form.products.find(p => p.id === product.id);

            if (productForm) {
                productForm.market_cost = market_cost;
                productForm.cost = cost;
                productForm.price = price;
                productForm.price_2plus = price_2plus;

                const effectiveCost = productForm.market_cost ?? productForm.cost;

                productForm.pricing_levels.forEach(pl => {
                    pl.price = this.calculatePricingLevel(pl, effectiveCost, price);
                })
            }
        },

        handlePriceLevelUpdate ({ pricingLevel, product }) {
            let productForm = this.form.products.find(p => p.id === product.id);
            let index = productForm.pricing_levels.findIndex(pl => pl.pricing_level_id === pricingLevel.pricing_level_id);

            if (index !== -1) {
                const effectiveCost = productForm.market_cost ?? productForm.cost;
                pricingLevel.price = this.calculatePricingLevel(pricingLevel, effectiveCost, productForm.price);

                if (pricingLevel.modifier_type === '' || pricingLevel.modifier_type === null || pricingLevel.modifier_type === 'disabled') {
                    pricingLevel.modifier_amount = null;
                }

                productForm.pricing_levels[index] = {
                    ...pricingLevel,
                    product_id: product.id
                }
            }
        },

        calculatePricingLevel (pricingLevel, cost, price) {
            return this.roundDecimals(calculatePricingLevel(pricingLevel, cost, price));
        },

        selectAll () {
            const productIds = this.products.data.map(p => p.id);

            this.selected.length = 0;

            if (this.selectedAll) {
                this.selected = this.selected.concat(productIds);
            }
        },

        resetFormData () {
            let data = [];

            this.products.data.forEach(product => {
                const productForm = {
                    id: product.id,
                    enabled: product.enabled,
                    local: product.local,
                    hot_buy: product.hot_buy,
                    special_order: product.special_order,
                    special_order_lead_hours: product.special_order
                        ? ([24, 48, 72].includes(Number(product.special_order_lead_hours)) ? Number(product.special_order_lead_hours) : 48)
                        : null,
                    allow_oversell: product.allow_oversell,
                    stock: product.stock,
                    market_cost: product.market_cost,
                    cost: product.cost,
                    price: product.price,
                    price_2plus: product.price_2plus,
                    pricing_levels: [],
                    product: product,
                    hasUpdated: false,
                    dirtyFields: [],
                    dirtyPricingLevelIds: [],
                };

                this.pricingLevels.forEach(l => {
                    const match = product.pricing.find(p => p.pricing_level_id === l.id);

                    productForm.pricing_levels.push({
                        product_id: product.id,
                        pricing_level_id: l.id,
                        pricing_level_title: l.title,
                        price: match ? match.price : null,
                        modifier_type: match ? match.modifier_type : null,
                        modifier_amount: match ? match.modifier_amount : null,
                    });
                });

                data.push(productForm);
            });

            return data;
        },

        isEditingPricingLevel(id) {
            return this.editingPriceLevels.findIndex(l => l === id) !== -1;
        },

        enablePricingLevelEdit (id) {
            this.editingPriceLevels.push(id);
        },

        disablePricingLevelEdit (id) {
            const index = this.editingPriceLevels.findIndex(l => l === id);

            if (index !== -1) {
                this.editingPriceLevels.splice(index, 1);
            }
        },
    },
}
</script>

<style scoped>
.multi-custom {
    min-height: 1.5rem;
}
</style>
